// handleDownloadPDF.js
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getComparisonRows } from './getComparisonRows';
import { getBeaconStatus } from './getBeaconStatus';

export function handleDownloadPDF(
  sortedApiReturn,
  includeNonValidated,
  includeOutboundBeacons,
  regressionTag,
) {
  // Filter the data based on settings
  let exportList = sortedApiReturn;

  // Separate missing Scenarios
  const missingEvents = exportList.filter((b) => b.isMissing);
  const nonMissingEvents = exportList.filter((b) => !b.isMissing);

  // Apply filters to non-missing Scenarios only
  let filteredNonMissing = nonMissingEvents;

  if (!includeNonValidated) {
    filteredNonMissing = filteredNonMissing.filter(
      (b) => b.truth.status !== 404 && b.beaconData.status !== 404,
    );
  }

  if (!includeOutboundBeacons) {
    filteredNonMissing = filteredNonMissing.filter((b) => b.beaconType === 'inbound');
  }

  // For missing Scenarios, only include inbound if outbound filter is off
  let filteredMissing = missingEvents;
  if (!includeOutboundBeacons) {
    filteredMissing = filteredMissing.filter((b) => b.beaconType === 'inbound');
  }

  // Combine filtered missing Scenarios with filtered non-missing Scenarios
  exportList = [...filteredMissing, ...filteredNonMissing].sort(
    (a, b) => a.timestamp - b.timestamp,
  );

  // Get test metadata
  const market = JSON.parse(localStorage.getItem('market') || '{}');
  const marketName = market.name || 'Unknown Market';
  const flowName = localStorage.getItem('lastFlow') || 'Unknown Flow';
  const startTime = localStorage.getItem('startTimeLocal') || 'N/A';
  const endTime = localStorage.getItem('endTimeLocal') || 'N/A';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get selected device information
  const selectedDevice = JSON.parse(localStorage.getItem('selectedDevice') || '{}');
  const deviceName = selectedDevice.name || 'No Device';
  const deviceId = selectedDevice.id || 'N/A';

  // Create PDF
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

  // Helper function to convert hex to RGB array for jsPDF-autotable
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  };

  // Colors matching Excel export
  const colors = {
    primary: '#211747',
    pass: '#2E7D32',
    passLight: '#E8F5E9',
    fail: '#C62828',
    failLight: '#FFEBEE',
    missing: '#E65100',
    missingLight: '#FFF3E0',
    extra: '#1565C0',
    extraLight: '#E3F2FD',
    notValidated: '#757575',
    notValidatedLight: '#F5F5F5',
  };

  let yPosition = 40;
  const margin = 40;

  // Title Page
  doc.setFontSize(24);
  doc.setTextColor(colors.primary);
  doc.text('Beacon Validation Report', margin, yPosition);
  yPosition += 40;

  // Test Information
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text('Test Information', margin, yPosition);
  yPosition += 25;

  doc.setFont(undefined, 'normal');
  doc.setFontSize(11);
  const testInfo = [
    ['Flow Name:', flowName],
    ['Market:', marketName],
    ['Device Name:', deviceName],
    ['Device ID:', deviceId],
    ['Regression Tag:', regressionTag || 'N/A'],
    ['Test Start:', `${startTime} (${timezone})`],
    ['Test End:', `${endTime} (${timezone})`],
  ];

  testInfo.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(value, margin + 100, yPosition);
    yPosition += 20;
  });

  yPosition += 20;

  // Scenario Summary
  doc.setFont(undefined, 'bold');
  doc.setFontSize(14);
  doc.text('Scenario Summary', margin, yPosition);
  yPosition += 25;

  doc.setFontSize(11);
  const summaryData = [
    ['Total Scenarios:', exportList.length.toString()],
    ['Inbound Scenarios:', exportList.filter((b) => b.beaconType === 'inbound').length.toString()],
    ['Outbound Scenarios:', exportList.filter((b) => b.beaconType !== 'inbound').length.toString()],
  ];

  summaryData.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont(undefined, 'normal');
    doc.text(value, margin + 120, yPosition);
    yPosition += 20;
  });

  yPosition += 20;

  // Validation Results
  doc.setFont(undefined, 'bold');
  doc.setFontSize(14);
  doc.text('Validation Results', margin, yPosition);
  yPosition += 25;

  doc.setFontSize(11);
  const validationResults = [
    [
      'Passed:',
      exportList.filter((b) => getBeaconStatus(b) === 'PASS').length.toString(),
      colors.pass,
    ],
    [
      'Failed:',
      exportList.filter((b) => getBeaconStatus(b) === 'FAIL').length.toString(),
      colors.fail,
    ],
    [
      'Missing Scenarios:',
      exportList.filter((b) => getBeaconStatus(b) === 'MISSING').length.toString(),
      colors.missing,
    ],
    [
      'Not Validated:',
      exportList.filter((b) => getBeaconStatus(b) === 'NOT_VALIDATED').length.toString(),
      colors.notValidated,
    ],
    [
      'Not Present In App:',
      exportList.filter((b) => getBeaconStatus(b) === 'NOT_PRESENT_IN_APP').length.toString(),
      colors.notValidated,
    ],
    ['Sequence Issues:', exportList.filter((b) => b.hasOrderIssue).length.toString(), colors.fail],
  ];

  validationResults.forEach(([label, value, color]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, margin, yPosition);
    doc.setFont(undefined, 'normal');
    if (color) {
      doc.setTextColor(color);
    }
    doc.text(value, margin + 120, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 20;
  });

  // New page for Scenarios Overview
  doc.addPage();
  yPosition = 40;

  doc.setFontSize(18);
  doc.setTextColor(colors.primary);
  doc.text('Scenarios Overview', margin, yPosition);
  yPosition += 35;

  // Create Scenarios table data
  const tableData = exportList.map((beacon) => {
    // Determine overall status
    const status = getBeaconStatus(beacon);

    const statusLabelMap = {
      PASS: 'Pass',
      FAIL: 'Fail',
      MISSING: 'Missing Event',
      NOT_VALIDATED: 'Not Validated',
      NOT_PRESENT_IN_APP: 'Not Present In App',
    };

    const statusColorMap = {
      PASS: colors.pass,
      FAIL: colors.fail,
      MISSING: colors.missing,
      NOT_VALIDATED: colors.notValidated,
      NOT_PRESENT_IN_APP: colors.notValidated,
    };

    const overallStatus = statusLabelMap[status] || status;

    const statusColor = statusColorMap[status] || null;

    const eventType = beacon.beaconType === 'inbound' ? 'Inbound' : `Outbound`;

    // Format timestamp - show "Missing" for missing Scenarios
    const formattedDate = beacon.isMissing
      ? 'Missing'
      : new Date(beacon.timestamp).toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });

    return [
      formattedDate,
      beacon.eventName || beacon.beaconID || '',
      eventType,
      beacon.stepName || '',
      overallStatus,
    ];
  });

  // Add table with autoTable
  autoTable(doc, {
    head: [['Date/Time', 'Scenario Name', 'Type', 'Event', 'Status']],
    body: tableData,
    startY: yPosition,
    theme: 'grid',
    headStyles: {
      fillColor: [33, 23, 71], // Primary color
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3,
    },
    columnStyles: {
      0: { cellWidth: 80 }, // Date/Time
      1: { cellWidth: 150 }, // Scenario Name
      2: { cellWidth: 60 }, // Type
      3: { cellWidth: 100 }, // Event
      4: { cellWidth: 80 }, // Status
    },
    didParseCell: (data) => {
      // Color code the status column
      if (data.section === 'body' && data.column.index === 4) {
        const status = data.cell.raw;

        if (status === 'Pass') {
          data.cell.styles.textColor = hexToRgb(colors.pass);
          data.cell.styles.fontStyle = 'bold';
        } else if (status === 'Fail') {
          data.cell.styles.textColor = hexToRgb(colors.fail);
          data.cell.styles.fontStyle = 'bold';
        } else if (status === 'Missing') {
          data.cell.styles.textColor = hexToRgb(colors.missing);
          data.cell.styles.fontStyle = 'bold';
        } else if (status === 'Not Validated') {
          data.cell.styles.textColor = hexToRgb(colors.notValidated);
        } else if (status.includes('Seq Issue')) {
          data.cell.styles.textColor = hexToRgb(colors.fail);
          data.cell.styles.fontStyle = 'bold';
        }
      }
    },
    margin: { left: margin, right: margin },
  });

  // Add detailed Scenario pages for all Scenarios
  const detailedEvents = exportList;

  detailedEvents.forEach((beacon, index) => {
    doc.addPage();
    yPosition = 40;

    // Scenario header
    doc.setFontSize(16);
    doc.setTextColor(colors.primary);
    doc.text(`Scenario ${index + 1}: ${beacon.eventName || beacon.beaconID}`, margin, yPosition);
    yPosition += 30;

    // Scenario details
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    const eventDetails = [
      [
        'Scenario Type:',
        beacon.beaconType === 'inbound' ? 'Inbound' : `Outbound (${beacon.beaconType})`,
      ],
      [
        'Timestamp:',
        beacon.isMissing
          ? 'Missing'
          : new Date(beacon.timestamp).toLocaleString('en-US', { timeZoneName: 'short' }),
      ],
      ['Device Name:', deviceName],
      ['Device ID:', deviceId],
      ['Event Name:', beacon.stepName || beacon.eventName || 'N/A'],
    ];

    eventDetails.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(value, margin + 80, yPosition);
      yPosition += 20;
    });

    // Overall status
    const status = getBeaconStatus(beacon);

    const statusLabelMap = {
      PASS: 'Pass',
      FAIL: 'Fail',
      MISSING: 'Missing Event',
      NOT_VALIDATED: 'Not Validated',
      NOT_PRESENT_IN_APP: 'Not Present In App',
    };

    const statusColorMap = {
      PASS: colors.pass,
      FAIL: colors.fail,
      MISSING: colors.missing,
      NOT_VALIDATED: colors.notValidated,
      NOT_PRESENT_IN_APP: colors.notValidated,
    };

    const overallStatus = statusLabelMap[status] || status;

    const statusColor = statusColorMap[status] || null;

    doc.setFont(undefined, 'bold');
    doc.text('Overall Status:', margin, yPosition);
    doc.setFont(undefined, 'normal');
    if (statusColor) {
      doc.setTextColor(statusColor);
    }
    doc.text(overallStatus, margin + 80, yPosition);
    doc.setTextColor(0, 0, 0);
    yPosition += 30;

    // Attribute comparison table
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Attribute Comparison', margin, yPosition);
    yPosition += 25;

    if (beacon.isMissing) {
      // For missing Scenarios, show what was expected
      const expectedData = [];
      if (beacon.truth && beacon.truth.data) {
        Object.entries(beacon.truth.data)
          .filter(([key]) => key !== 'market')
          .forEach(([key, val]) => {
            const expectedValue = val && val.value !== undefined ? val.value : val;
            expectedData.push([key, String(expectedValue), 'Missing']);
          });
      }

      if (expectedData.length > 0) {
        autoTable(doc, {
          head: [['Variable Name', 'Expected Value', 'Status']],
          body: expectedData,
          startY: yPosition,
          theme: 'grid',
          headStyles: {
            fillColor: [33, 23, 71],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 9,
          },
          columnStyles: {
            2: {
              cellWidth: 60,
              halign: 'center',
            },
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              // Bold all cells for missing Scenarios
              data.cell.styles.fontStyle = 'bold';
              // Color code the status column
              if (data.column.index === 2) {
                data.cell.styles.textColor = hexToRgb(colors.missing);
              }
            }
          },
          margin: { left: margin, right: margin },
        });
      }
    } else if (['NOT_VALIDATED', 'NOT_PRESENT_IN_APP'].includes(getBeaconStatus(beacon))) {
      // For not validated Scenarios, show actual values only
      const actualData = [];
      Object.entries(beacon.beaconData.data)
        .filter(([key]) => key !== 'market')
        .forEach(([key, val]) => {
          const actual = val instanceof Object ? val.value : val;
          actualData.push([key, String(actual), 'Extra']);
        });

      if (actualData.length > 0) {
        autoTable(doc, {
          head: [['Variable Name', 'Actual Value', 'Status']],
          body: actualData,
          startY: yPosition,
          theme: 'grid',
          headStyles: {
            fillColor: [33, 23, 71],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 9,
          },
          columnStyles: {
            2: {
              cellWidth: 60,
              halign: 'center',
            },
          },
          didParseCell: (data) => {
            // Color code the status column (Extra Scenarios are NOT bolded)
            if (data.section === 'body' && data.column.index === 2) {
              data.cell.styles.textColor = hexToRgb(colors.extra);
            }
          },
          margin: { left: margin, right: margin },
        });
      }
    } else {
      // For validated Scenarios, show full comparison
      const rows = getComparisonRows(beacon.truth, beacon.beaconData, beacon.modifiedRows || {});

      // Sort: custom attributes first, then others, then Extra attributes last
      rows.sort((a, b) => {
        // Extra attributes go to the bottom
        if (a.passFail === 'Extra' && b.passFail !== 'Extra') return 1;
        if (a.passFail !== 'Extra' && b.passFail === 'Extra') return -1;

        // For non-Extra attributes, custom_attributes come first
        const aPr = a.variableName.includes('data.custom_attributes') ? 0 : 1;
        const bPr = b.variableName.includes('data.custom_attributes') ? 0 : 1;
        if (aPr !== bPr) return aPr - bPr;

        // Within each group, sort alphabetically
        return a.variableName.localeCompare(b.variableName);
      });

      const comparisonData = rows.map((row) => [
        row.variableName,
        String(row.expectedValue || ''),
        String(row.actualValue || ''),
        row.passFail,
      ]);

      if (comparisonData.length > 0) {
        autoTable(doc, {
          head: [['Variable', 'Expected', 'Actual', 'Status']],
          body: comparisonData,
          startY: yPosition,
          theme: 'grid',
          headStyles: {
            fillColor: [33, 23, 71],
            textColor: [255, 255, 255],
            fontSize: 10,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 8,
          },
          columnStyles: {
            0: { cellWidth: 140 },
            1: { cellWidth: 120 },
            2: { cellWidth: 120 },
            3: {
              cellWidth: 60,
              halign: 'center',
            },
          },
          didParseCell: (data) => {
            if (data.section === 'body') {
              const status = data.row.raw[3];
              const variableName = data.row.raw[0];

              // Color extra custom_attributes red (attribute name column)
              const isCustomAttribute = variableName.includes('data.custom_attributes');
              const isExtra = status === 'Extra';

              if (isCustomAttribute && isExtra && data.column.index === 0) {
                data.cell.styles.textColor = [211, 47, 47]; // RGB for #D32F2F
                data.cell.styles.fontStyle = 'bold';
              }
              // Bold all non-Extra attributes (Pass, Fail, and Missing)
              else if (status !== 'Extra' && data.column.index === 0) {
                data.cell.styles.fontStyle = 'bold';
              }

              // Color code the status column
              if (data.column.index === 3) {
                if (status === 'Pass') {
                  data.cell.styles.textColor = hexToRgb(colors.pass);
                } else if (status === 'Fail') {
                  data.cell.styles.textColor = hexToRgb(colors.fail);
                } else if (status === 'Extra') {
                  data.cell.styles.textColor = hexToRgb(colors.extra);
                } else if (status === 'Missing') {
                  data.cell.styles.textColor = hexToRgb(colors.missing);
                }
              }
            }
          },
          margin: { left: margin, right: margin },
        });
      }
    }
  });

  // Generate filename with timestamp
  const now = new Date();
  const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}`;

  // Save the PDF
  doc.save(`beacon_validation_${timestamp}.pdf`);
}
